import React, { useState, useEffect } from 'react';
import '../css/sg-platform.css';
import '../css/sg-pilars.css';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Checkbox,
  TextField,
  Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';

import {
  fetchData_getPilar_Questionario,
  postQuestionnaireResponses,
  setSubmittedCertificate,
  getExploracaoAndCulturaFromCertificacao as getDataFromCertificacao,
} from '../services/fetch_api';
// import { useAuth } from '../services/authContext'; // Import the useAuth hook

const Questionario = ({
  pilar_id,
  certification_id,
  cultura_tipo,
  respostas,
  updatePilarState, // Ensure this prop is received
}) => {
  // State for holding Questionario data, loading status, and errors
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState({});
  const [checkboxState, setCheckboxState] = useState({});
  const [numericInput, setNumericInput] = useState({});
  const [stringInput, setStringInput] = useState({});
  const [nextPilarId, setNextPilarId] = useState(null);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [exploracao_id, setExploracaoId] = useState(null); // Set the exploracao_id
  const [cultura_id, setCulturaId] = useState(null); // Set the exploracao_id
  const [dateCreated, setDateCreated] = useState(null); // Set the exploracao_id
  const [missingMandatoryIndicators, setMissingMandatoryIndicators] = useState(
    []
  ); // Track missing mandatory indicators

  // const { token } = useAuth(); // Get the token from the AuthContext

  // Fetch data from the API when the component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        const exploracao_cultura = await getDataFromCertificacao(
          certification_id
        );
        setExploracaoId(exploracao_cultura.exploracao_id); // Set the exploracao_id
        setCulturaId(exploracao_cultura.cultura_id); // Set the exploracao_id
        setDateCreated(exploracao_cultura.data_creacao_certificacao); // Set the data_criacao

        // console.log('Fetching data for pilar_id:', pilar_id); // Debug log
        const response = await fetchData_getPilar_Questionario(
          pilar_id,
          cultura_tipo
        );
        // console.log('Raw API Response:', response); // Debug log

        if (!response || response.length === 0) {
          console.warn('Empty or invalid response for pilar:', pilar_id);
          setRawData([]);
          setLoading(false);
          return;
        }

        setRawData(response);
        setLoading(false); // Disable loading spinner

        // Initialize checkboxState and numericInput with respostas
        const initialCheckboxState = {};
        const initialNumericInput = {};
        const initialStringInput = {};

        respostas.forEach((resposta) => {
          if (resposta.tipo === 'radio') {
            initialCheckboxState[resposta.indicador_id] = {
              checkbox_sim: resposta.resposta === 'Sim',
              checkbox_nao: resposta.resposta === 'Não',
            };
          } else if (resposta.tipo === 'numeric') {
            initialNumericInput[resposta.indicador_id] = resposta.resposta;
          } else if (resposta.tipo === 'text') {
            initialStringInput[resposta.indicador_id] = resposta.resposta;
          }
        });

        setCheckboxState(initialCheckboxState);
        setNumericInput(initialNumericInput);
        setStringInput(initialStringInput);
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message || 'Error fetching data in Questionario');
        setLoading(false);
      }
    };
    fetchData(); // Call the function to fetch data
  }, [certification_id, cultura_tipo, pilar_id, respostas]); // Add respostas to the dependency array

  // Function to transform the raw data into the required format
  const transformData = (rawData) => {
    // console.log('Transforming data for pilar_id:', pilar_id); // Debug log
    // console.log('Raw data to transform:', rawData); // Debug log

    if (!rawData || !Array.isArray(rawData)) {
      console.warn('Invalid rawData received for pilar:', pilar_id, rawData);
      return [];
    }

    const groupedData = [];

    rawData.forEach((item) => {
      const {
        id,
        texto,
        subgrupo,
        grupo,
        tipo,
        pilar,
        ordem,
        justificacao,
        certificado,
        obrigatorio,
      } = item;

      // Find or create the group
      let grupoData = groupedData.find((g) => g.grupoName === grupo);
      if (!grupoData) {
        grupoData = {
          grupoId: groupedData.length + 1, // Assign a unique ID based on position
          grupoName: grupo,
          subgrupos: [],
          pilar: pilar,
        };
        groupedData.push(grupoData);
      }

      // Find or create the subgroup within the group
      let subgrupoData = grupoData.subgrupos.find(
        (s) => s.subgrupoName === subgrupo
      );
      if (!subgrupoData) {
        subgrupoData = {
          subgrupoName: subgrupo,
          indicadores: [],
        };
        grupoData.subgrupos.push(subgrupoData);
      }

      // Add the question (indicador) to the appropriate subgroup
      subgrupoData.indicadores.push({
        indicadorId: id,
        justificacao: justificacao,
        pergunta: texto,
        resposta: '', // Default empty, can be filled in later
        ordem: ordem,
        tipo: tipo,
        certificado: certificado,
        obrigatorio: obrigatorio,
      });
    });

    return groupedData;
  };

  // Transformed data
  const structuredData = transformData(rawData);
  /* 
  if (structuredData.length === 0) {
    return <div>No data available</div>; // Handle empty data case
  } */

  // Show a loading spinner while data is being fetched
  if (loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}
      >
        <CircularProgress />
      </div>
    );
  }

  // Modified handler to ensure only one checkbox is selected at a time
  const handleCheckboxChange = (event, indicadorId) => {
    const { name, checked } = event.target;
    const previousState = checkboxState[indicadorId] || {
      checkbox_sim: false,
      checkbox_nao: false,
    };

    // Clear missing mandatory indicator when user provides input
    if (checked && missingMandatoryIndicators.includes(indicadorId)) {
      setMissingMandatoryIndicators((prev) =>
        prev.filter((id) => id !== indicadorId)
      );
    }

    // If unchecking, allow it
    if (!checked) {
      setCheckboxState((prevState) => ({
        ...prevState,
        [indicadorId]: {
          ...previousState,
          [name]: false,
        },
      }));
      return;
    }

    // If checking, ensure other option is unchecked
    setCheckboxState((prevState) => ({
      ...prevState,
      [indicadorId]: {
        checkbox_sim: name === 'checkbox_sim',
        checkbox_nao: name === 'checkbox_nao',
      },
    }));
  };

  const handleNumericInputChange = (event, indicadorId) => {
    const { value } = event.target;

    // Clear missing mandatory indicator when user provides input
    if (
      value.trim() !== '' &&
      missingMandatoryIndicators.includes(indicadorId)
    ) {
      setMissingMandatoryIndicators((prev) =>
        prev.filter((id) => id !== indicadorId)
      );
    }

    // Validation: Ensure the input is not empty
    if (value.trim() === '') {
      setError((prevErrors) => ({
        ...prevErrors,
        [indicadorId]: 'Este campo é obrigatório.',
      }));
      return;
    }

    // Validation: Ensure only positive numbers
    if (!/^\d+(\.\d+)?$/.test(value)) {
      setError((prevErrors) => ({
        ...prevErrors,
        [indicadorId]: 'Só é permitido números positivos.',
      }));
      return;
    }

    // Validation: Ensure the number is not greater than 100
    let indicador = structuredData
      .flatMap((group) => group.subgrupos)
      .flatMap((subgrupo) => subgrupo.indicadores)
      .find((indicador) => indicador.indicadorId === indicadorId);
    if (
      indicador &&
      indicador.pergunta &&
      indicador.pergunta.includes('percentagem')
    ) {
      // Validation: Ensure the input is between 0 and 100
      const numericValue = parseFloat(value);
      if (numericValue < 0 || numericValue > 100) {
        setError((prevErrors) => ({
          ...prevErrors,
          [indicadorId]: 'O valor deve estar entre 0 e 100.',
        }));
        return;
      }
    }

    // Clear any previous error if input is valid
    setError((prevErrors) => ({
      ...prevErrors,
      [indicadorId]: '',
    }));

    setNumericInput((prevState) => ({
      ...prevState,
      [indicadorId]: value,
    }));
  };

  const handleStringInputChange = (event, indicadorId) => {
    const { value } = event.target;

    // Clear missing mandatory indicator when user provides input
    if (
      value.trim() !== '' &&
      missingMandatoryIndicators.includes(indicadorId)
    ) {
      setMissingMandatoryIndicators((prev) =>
        prev.filter((id) => id !== indicadorId)
      );
    }

    // Validation: Ensure the input is not empty
    if (value.trim() === '') {
      setError((prevErrors) => ({
        ...prevErrors,
        [indicadorId]: 'Este campo é obrigatório.',
      }));
      return;
    }

    // Validation: Check for specific format (12,12,12) or (12, 12, 12)
    const validFormat =
      /^\(\s*\d+(\.\d+)?\s*,\s*\d+(\.\d+)?\s*,\s*\d+(\.\d+)?\s*\)$/;
    if (!validFormat.test(value)) {
      setError((prevErrors) => ({
        ...prevErrors,
        [indicadorId]:
          'O formato deve ser (N,P,K) ou (N, P, K), com os respetivos valores.',
      }));
      return;
    }

    // Clear any previous error if input is valid
    setError((prevErrors) => ({
      ...prevErrors,
      [indicadorId]: '',
    }));

    setStringInput((prevState) => ({
      ...prevState,
      [indicadorId]: value,
    }));
  };

  // Function to gather responses and structure data for submission
  const gatherResponses = async () => {
    const responses = [];

    structuredData.forEach((group) => {
      group.subgrupos.forEach((subgrupo) => {
        subgrupo.indicadores.forEach((indicador) => {
          const checkboxValues = checkboxState[indicador.indicadorId] || {};
          const numericValue = numericInput[indicador.indicadorId] || '';
          const stringValue = stringInput[indicador.indicadorId] || '';
          const resposta =
            indicador.tipo === 'radio'
              ? checkboxValues.checkbox_sim
                ? 'Sim'
                : checkboxValues.checkbox_nao
                ? 'Não'
                : ''
              : indicador.tipo === 'numeric'
              ? numericValue
              : indicador.tipo === 'text'
              ? stringValue
              : '';

          // Adiciona ao array de respostas apenas se houver uma resposta válida
          if (resposta !== '') {
            responses.push({
              indicador_id: indicador.indicadorId,
              // tipo: indicador.tipo,
              resposta,
            });
          }
        });
      });
    });

    // console.log('Responses:', responses); // Substituir por chamada à API

    const payload = {
      exploracao_id: exploracao_id,
      certificado_id: certification_id,
      respostas: responses,
    };

    try {
      await postQuestionnaireResponses(payload);
      alert('Dados guardados com sucesso!');
    } catch (err) {
      console.error('Error saving data:', err);
      alert(err.response.data.error);
    }
  };

  const checkMandatoryQuestions = () => {
    const unansweredMandatoryQuestions = [];

    structuredData.forEach((group) =>
      group.subgrupos.forEach((subgrupo) =>
        subgrupo.indicadores.forEach((indicador) => {
          if (indicador.obrigatorio) {
            const checkboxValues = checkboxState[indicador.indicadorId] || {};
            const numericValue = numericInput[indicador.indicadorId] || '';
            const stringValue = stringInput[indicador.indicadorId] || '';
            const resposta =
              indicador.tipo === 'radio'
                ? checkboxValues.checkbox_sim || checkboxValues.checkbox_nao
                : indicador.tipo === 'numeric'
                ? numericValue
                : indicador.tipo === 'text'
                ? stringValue
                : '';
            if (!resposta) {
              unansweredMandatoryQuestions.push(indicador.indicadorId);
            }
          }
        })
      )
    );

    return unansweredMandatoryQuestions;
  };

  const checkAndHighlightMandatoryQuestions = () => {
    const unansweredMandatoryQuestions = checkMandatoryQuestions();
    if (unansweredMandatoryQuestions.length > 0) {
      // Store the missing mandatory indicators for highlighting
      setMissingMandatoryIndicators(unansweredMandatoryQuestions);

      alert('Por favor, responda a todas as perguntas obrigatórias.');
      setCheckboxState((prevState) => {
        const newState = { ...prevState };
        unansweredMandatoryQuestions.forEach((id) => {
          newState[id] = {
            checkbox_sim: false,
            checkbox_nao: false,
          };
        });
        return newState;
      });
      setNumericInput((prevState) => {
        const newState = { ...prevState };
        unansweredMandatoryQuestions.forEach((id) => {
          newState[id] = '';
        });
        return newState;
      });
      setStringInput((prevState) => {
        const newState = { ...prevState };
        unansweredMandatoryQuestions.forEach((id) => {
          newState[id] = '';
        });
        return newState;
      });
      return true;
    } else {
      // Clear the missing indicators when all are filled
      setMissingMandatoryIndicators([]);
    }
    return false;
  };

  const setSubmittedQuestionaire = async () => {
    // Check if all mandatory questions are answered and highlight them if not
    if (checkAndHighlightMandatoryQuestions()) {
      return;
    }

    try {
      await setSubmittedCertificate(
        certification_id,
        exploracao_id,
        cultura_id,
        dateCreated
      );
      alert('Certificate state changed to SUBMETIDO successfully!');
    } catch (error) {
      console.error('Error submitting certificate:', error);
      alert('Failed to submit certificate: ' + error.message);
    }
  };

  const isLastPilar = pilar_id >= 4; // Assuming there are 4 pilares

  const continueResponses = async () => {
    // Check if all mandatory questions are answered before continuing
    if (checkAndHighlightMandatoryQuestions()) {
      return; // Stop execution if there are missing mandatory indicators
    }

    await gatherResponses();
    const newPilarId = pilar_id + 1;
    setNextPilarId(newPilarId); // Set the next pilar_id
    if (typeof updatePilarState === 'function') {
      updatePilarState(newPilarId); // Update the pilar state in Pilares component
    } else {
      console.error('updatePilarState is not a function');
    }
  };

  const submitResponses = async () => {
    try {
      setIsSubmitDisabled(true); // Disable the button initially

      // Check if all mandatory questions are answered before submitting
      if (checkAndHighlightMandatoryQuestions()) {
        setIsSubmitDisabled(false); // Re-enable the button if validation fails
        return; // Stop execution if there are missing mandatory indicators
      }

      await gatherResponses();
      await setSubmittedQuestionaire(); // Call the setSubmittedQuestions function
      /* alert('All responses submitted successfully!'); */
      // Redirect to Dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Error submitting responses:', error);
      alert('Error submitting responses: ' + error.message);
      setIsSubmitDisabled(false); // Re-enable the button if submission fails
    }
  };

  return (
    <section className="pilar-container">
      {nextPilarId ? (
        <Questionario
          pilar_id={nextPilarId}
          certification_id={certification_id}
          cultura_tipo={cultura_tipo}
          respostas={respostas}
          updatePilarState={updatePilarState}
        />
      ) : (
        <>
          <h2>
            Indicadores - Pilar{' '}
            {structuredData && structuredData[0]
              ? structuredData[0].pilar
              : pilar_id}
          </h2>
          {!structuredData || structuredData.length === 0 ? (
            <div>Não há dados disponíveis para este pilar.</div>
          ) : (
            <>
              <br />
              {structuredData.map((group) => (
                <TableContainer
                  className="table-container"
                  component={Paper}
                  key={group.grupoId}
                >
                  {/* --------------- O nome do grupo é o título --------------- */}

                  <Typography className="group-title">
                    {group.grupoName}
                  </Typography>

                  {/* --------------- Subtítulos --------------- */}

                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell className="column-title">Subgrupo</TableCell>
                        <TableCell className="column-title">
                          Indicador
                        </TableCell>
                        <TableCell className="column-title">
                          Obrigatório
                        </TableCell>
                        <TableCell className="column-title">
                          Presente na Certificação
                        </TableCell>
                        <TableCell className="column-title">Info</TableCell>
                        <TableCell className="column-title">Sim</TableCell>
                        <TableCell className="column-title">Não</TableCell>
                      </TableRow>
                    </TableHead>

                    {/* --------------- Conteúdo da Tabela --------------- */}

                    <TableBody>
                      {group.subgrupos.map((subgrupo, grupoIndex) => (
                        <React.Fragment key={grupoIndex}>
                          {subgrupo.indicadores.map(
                            (indicador, subgrupoIndex) => {
                              // Check if this indicator is missing and mandatory
                              const isMissingMandatory =
                                missingMandatoryIndicators.includes(
                                  indicador.indicadorId
                                );

                              return (
                                <TableRow
                                  key={`${indicador.indicadorId}-${grupoIndex}-${subgrupoIndex}`}
                                  className={
                                    isMissingMandatory
                                      ? 'missing-mandatory-row'
                                      : ''
                                  }
                                >
                                  {/* --------------- Subgrupo --------------- */}

                                  {subgrupoIndex === 0 && (
                                    <TableCell
                                      className="subgroup-column"
                                      rowSpan={subgrupo.indicadores.length}
                                    >
                                      {subgrupo.subgrupoName}
                                    </TableCell>
                                  )}

                                  {/* --------------- Indicador --------------- */}

                                  <TableCell
                                    className={`indicador-column ${
                                      isMissingMandatory
                                        ? 'missing-mandatory-indicator'
                                        : ''
                                    }`}
                                  >
                                    {indicador.pergunta}
                                  </TableCell>

                                  {/* --------------- Obrigatório --------------- */}
                                  <TableCell className="obrigatorio-column">
                                    {indicador.obrigatorio ? (
                                      <i
                                        className={`fa-solid fa-check ${
                                          isMissingMandatory
                                            ? 'missing-mandatory-checkbox'
                                            : ''
                                        }`}
                                      ></i>
                                    ) : (
                                      ''
                                    )}
                                  </TableCell>

                                  {/* --------------- Presente na Certificação --------------- */}

                                  <TableCell className="certification-column">
                                    {indicador.certificado
                                      ? indicador.certificado
                                          .split(', ')
                                          .map((cert, index) => (
                                            <div key={index}>{cert}</div>
                                          ))
                                      : '-'}
                                  </TableCell>

                                  {/* --------------- Info Icon --------------- */}

                                  <TableCell className="info-column">
                                    <Tooltip
                                      title={indicador.justificacao}
                                      arrow
                                    >
                                      <InfoIcon id="info-icon" />
                                    </Tooltip>
                                  </TableCell>

                                  {/* --------------- Coluna Sim --------------- */}

                                  {indicador.tipo === 'radio' ? (
                                    <>
                                      <TableCell
                                        className="sim-column"
                                        id="sim-column"
                                      >
                                        <Checkbox
                                          checked={
                                            checkboxState[indicador.indicadorId]
                                              ?.checkbox_sim ||
                                            (respostas.find(
                                              (resposta) =>
                                                resposta.indicador.id ===
                                                  indicador.indicadorId &&
                                                resposta.resposta === 'Sim'
                                            ) !== undefined &&
                                              !checkboxState[
                                                indicador.indicadorId
                                              ])
                                          }
                                          name="checkbox_sim"
                                          onChange={(event) => {
                                            handleCheckboxChange(
                                              event,
                                              indicador.indicadorId
                                            );
                                          }}
                                        />
                                      </TableCell>

                                      {/* --------------- Coluna Não & Numérica --------------- */}

                                      <TableCell
                                        className="nao-column"
                                        id="nao-column"
                                      >
                                        <Checkbox
                                          checked={
                                            checkboxState[indicador.indicadorId]
                                              ?.checkbox_nao ||
                                            (respostas.find(
                                              (resposta) =>
                                                resposta.indicador.id ===
                                                  indicador.indicadorId &&
                                                resposta.resposta === 'Não'
                                            ) !== undefined &&
                                              !checkboxState[
                                                indicador.indicadorId
                                              ])
                                          }
                                          name="checkbox_nao"
                                          onChange={(event) => {
                                            handleCheckboxChange(
                                              event,
                                              indicador.indicadorId
                                            );
                                          }}
                                        />
                                      </TableCell>
                                    </>
                                  ) : indicador.tipo === 'numeric' ? (
                                    <TableCell colSpan={3}>
                                      <TextField
                                        id="numeric-input"
                                        type="number"
                                        defaultValue={
                                          respostas.find(
                                            (resposta) =>
                                              resposta.indicador.id ===
                                              indicador.indicadorId
                                          )?.resposta || ''
                                        }
                                        onChange={(event) =>
                                          handleNumericInputChange(
                                            event,
                                            indicador.indicadorId
                                          )
                                        }
                                        label="Insira um valor"
                                        variant="outlined"
                                        fullWidth
                                        error={!!error[indicador.indicadorId]}
                                        helperText={
                                          typeof error[
                                            indicador.indicadorId
                                          ] === 'string'
                                            ? error[indicador.indicadorId]
                                            : ''
                                        }
                                      />
                                    </TableCell>
                                  ) : indicador.tipo === 'text' ? (
                                    <TableCell colSpan={3}>
                                      <TextField
                                        id="string-input"
                                        type="text"
                                        defaultValue={
                                          respostas.find(
                                            (resposta) =>
                                              resposta.indicador.id ===
                                              indicador.indicadorId
                                          )?.resposta || ''
                                        }
                                        onChange={(event) =>
                                          handleStringInputChange(
                                            event,
                                            indicador.indicadorId
                                          )
                                        }
                                        label="Insira os valores (N,P,K)"
                                        variant="outlined"
                                        fullWidth
                                        error={!!error[indicador.indicadorId]}
                                        helperText={
                                          typeof error[
                                            indicador.indicadorId
                                          ] === 'string'
                                            ? error[indicador.indicadorId]
                                            : ''
                                        }
                                      />
                                    </TableCell>
                                  ) : null}
                                </TableRow>
                              );
                            }
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ))}

              {/* --------------- Botões --------------- */}

              <section class="buttons-container">
                <a href="#.html">
                  <button
                    type="button"
                    class="btn-save-edit"
                    onClick={gatherResponses}
                  >
                    Salvar
                    <i class="fa-solid fa-floppy-disk"></i>
                  </button>
                </a>
                {/* {console.log('isSubmitDisabled:', isSubmitDisabled)} */}{' '}
                {/* Add this log */}
                <a href="#.html">
                  <button
                    type="button"
                    class="btn-upload"
                    onClick={isLastPilar ? submitResponses : continueResponses}
                    disabled={isSubmitDisabled}
                  >
                    {isLastPilar ? 'Submeter' : 'Continuar'}
                    <i
                      class={`fa-solid ${
                        isLastPilar ? 'fa-check' : 'fa-chevron-right'
                      }`}
                    ></i>
                  </button>
                </a>
              </section>
            </>
          )}
        </>
      )}
    </section>
  );
};

export default Questionario;
